import React from "react";
import * as moment from "moment";
import { canPickDirectory } from "../../utils/downloadMediaToFolder";
import { getLocaleForLanguage } from "../../utils/composerUtils";
import "./MediaSelectionBar.css";

function ActionButton({
  accent,
  isInternal,
  emailTheme,
  cancelMode,
  requestStyle,
  title,
  disabled,
  onClick,
  icon,
  iconFilled,
  children,
  minWidth,
}) {
  const toneClass = cancelMode
    ? "kj-btn--secondary"
    : `kj-btn--outline${emailTheme ? " kj-btn--email" : isInternal ? " kj-btn--internal" : ""}${requestStyle ? " kj-btn--on-surface" : ""}`;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`kj-btn ${toneClass}${minWidth ? " media-selection-bar__action--wide" : ""}`}
    >
      <span
        className={`material-symbols-outlined media-selection-bar__action-icon${iconFilled ? " media-selection-bar__action-icon--filled" : ""}`}
      >
        {icon}
      </span>
      <span>{children}</span>
    </button>
  );
}

function formatDownloadTime(timestamp, language) {
  const locale = getLocaleForLanguage(language);
  return moment(timestamp).locale(locale).format("L, LT");
}

function interpolate(template, vars) {
  return Object.keys(vars).reduce(
    (text, key) => text.replace(new RegExp(`\\{${key}\\}`, "g"), vars[key]),
    template
  );
}

export default function MediaSelectionBar({
  isSelect,
  selectedCount,
  isDownloading,
  accent,
  isInternal,
  emailTheme,
  strings,
  language,
  visibleCount,
  allVisibleSelected,
  onToggleSelect,
  onToggleSelectAll,
  onDownload,
  downloadJob,
  onDismissNote,
  // The caller shows its own select / cancel toggle; the bar then only appears while
  // selecting or downloading.
  hideSelectToggle = false,
}) {
  const folderPicker = canPickDirectory();
  const downloadLabel = folderPicker
    ? strings.download_to_folder || "Download to folder"
    : strings.download_as_zip || "Download as ZIP";
  const selectLabel = isSelect ? strings.cancel : strings.select_files;
  const selectAllLabel = allVisibleSelected
    ? strings.deselect_all || "Deselect All"
    : strings.select_all || "Select All";

  const jobRunning = downloadJob?.status === "running";
  const jobCompleted = downloadJob?.status === "completed" && downloadJob?.completedAt;
  const selectDisabled = isDownloading || (!isSelect && !(visibleCount > 0));
  const percent = Math.max(0, Math.min(100, downloadJob?.percent || 0));
  const completedCount = downloadJob?.completedCount || 0;
  const totalCount = downloadJob?.totalCount || 0;
  const noteTemplate =
    downloadJob?.kind === "docs"
      ? strings.docs_downloaded_on || "Documents downloaded on {datetime}"
      : strings.media_downloaded_on || "Images downloaded on {datetime}";

  if (hideSelectToggle && !isSelect && !jobRunning && !jobCompleted) return null;

  return (
    <div className="media-selection-bar" style={{ "--media-selection-bar-accent": accent }}>
      {jobRunning && (
        <div className="media-download-status">
          <div className="media-download-status-copy">
            <span className="media-download-status-title">
              {interpolate(
                strings.media_download_progress || "Downloading {current} of {total}",
                { current: String(Math.min(completedCount + 1, totalCount)), total: String(totalCount) }
              )}
            </span>
            <span className="media-download-status-pct">{percent}%</span>
          </div>
          <div className="media-download-track" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} role="progressbar">
            <div
              className="media-download-fill media-selection-bar__fill"
              style={{ "--media-selection-bar-progress": `${percent}%` }}
            />
          </div>
        </div>
      )}
      {jobCompleted && (
        <div className="media-download-note">
          <span className="material-symbols-outlined media-selection-bar__done-icon">
            check_circle
          </span>
          <span className="media-download-note-text">
            {interpolate(noteTemplate, {
              datetime: formatDownloadTime(downloadJob.completedAt, language),
            })}
          </span>
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
            onClick={onDismissNote}
            title={strings.cancel || "Close"}
            aria-label={strings.cancel || "Close"}
          >
            <span className="material-symbols-outlined media-selection-bar__close-icon">
              close
            </span>
          </button>
        </div>
      )}
      {!jobRunning && (!hideSelectToggle || isSelect) && (
        <div className="media-selection-actions">
          {!hideSelectToggle && (
          <ActionButton
            accent={accent}
            isInternal={isInternal}
            emailTheme={emailTheme}
            cancelMode={isSelect}
            requestStyle={!isSelect && isInternal && !emailTheme}
            title={selectLabel}
            disabled={selectDisabled}
            onClick={onToggleSelect}
            icon={isSelect ? "cancel" : "library_add_check"}
            iconFilled
          >
            {selectLabel}
          </ActionButton>
          )}
          {isSelect && visibleCount > 0 && !isDownloading && (
            <ActionButton
              accent={accent}
              isInternal={isInternal}
              emailTheme={emailTheme}
              title={selectAllLabel}
              onClick={onToggleSelectAll}
              icon={allVisibleSelected ? "deselect" : "select_all"}
            >
              {selectAllLabel}
            </ActionButton>
          )}
          {isSelect && selectedCount > 0 && !isDownloading && (
            <ActionButton
              accent={accent}
              isInternal={isInternal}
              emailTheme={emailTheme}
              title={downloadLabel}
              onClick={onDownload}
              icon={folderPicker ? "folder" : "folder_zip"}
              minWidth={160}
            >
              {`${downloadLabel} (${selectedCount})`}
            </ActionButton>
          )}
        </div>
      )}
    </div>
  );
}
